import $ from "jquery";
import Snap from "snapsvg-cjs-ts";
import { MapView } from "./Basemap";
import { ForecastTime, InitialTime, ShadeSelector } from "./Controller";
import {
    InputNumber,
    OnOffButton,
    setFeatureAvailability,
} from "./Form";
import { readFlag } from "./UrlState";
import { resourcePath } from "./paths";
import { getMapMeta } from "./MapMeta";

interface WindPointData {
    W: number[];
    D: number[];
}

type WindPointResponse = Record<string, WindPointData>;

const PRESETS: Record<string, { keys: string; size: number }> = {
    Lv0: {
        size: 0.9,
        keys: "M289317,M162372,M136410,M089351,M133336,M169334,M202315,M233296,M251267,M284244,M273212,M306152,M345137,M383177,M510048,M501084,M479123,M431156,M346207,M336227,M332252,M315270,M308300,M339318,M262335,M235345,M207353,M484179,M357160,M325191,M308241,M390325,M458355",
    },
    Lv1: {
        size: 1.1,
        keys: "M289317,M162372,M136410,M089351,M133336,M169334,M202315,M233296,M251267,M284244,M273212,M306152,M345137,M383177,M510048,M501084,M479123,M431156,M346207,M336227,M332252,M315270,M308300,M339318,M262335,M235345,M207353,M484179,M357160,M325191,M308241,M390325,M458355,M116379,M269309,M285287,M296197,M452138,M511067,M406157",
    },
    Lv2: {
        size: 1.2,
        keys: "M289317,M162372,M136410,M089351,M133336,M169334,M202315,M233296,M251267,M284244,M273212,M306152,M345137,M383177,M510048,M501084,M479123,M431156,M346207,M336227,M332252,M315270,M308300,M339318,M262335,M235345,M207353,M484179,M357160,M325191,M308241,M390325,M458355,M116379,M269309,M285287,M296197,M452138,M511067,M406157,M288336,M325165,M362184,M312214,M288226,M295258,M128361,M151314,M237322,M190334,M111427",
    },
    Kanto: {
        size: 1.3,
        keys: "M289317,M307304,M262335,M286334,M274330,M299322,M269319,M272305,M267292,M279289,M279312,M287297,M295307,M309292,M290285,M305281,M265276,M279275,M297271,M317316,M265352,M281347,M278364,M316267,M283255,M301257,M261306,M321283,M269264,M261317,M284307,M277321,M280301",
    },
};

export class PointArrows {
    private readonly onOff = new OnOffButton("arrow");
    private readonly size = new InputNumber("#input_Asiz", {
        value: 0.9,
        min: 0,
    });
    private readonly yellowWidth = new InputNumber("#input_Aylw", {
        value: 20,
        min: 0,
    });
    private readonly temperatureAdjustment = new InputNumber("#input_Aadj", {
        value: 0,
    });
    private readonly arrows = Snap("#arrows_a");
    private readonly values = Snap("#arrows_v");
    private data: WindPointResponse = {};
    private keys: string[] = [];
    private savedKeys = PRESETS.Lv0.keys.split(",");
    private sequence = 0;
    private lastRightDown: { time: number; x: number; y: number } | null = null;
    private changeCallback: (task: Promise<unknown>) => void = () => {};

    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
        private readonly forecastTime: ForecastTime,
        private readonly mapView: MapView,
    ) {
        this.installSymbol();
        this.loadParams();
        this.onOff.onChange(() => this.onEnabledChanged());
        this.size.onChange(() => this.render());
        this.yellowWidth.onChange(() => this.render());
        this.temperatureAdjustment.onChange(() => this.render());
        for (const name of Object.keys(PRESETS)) {
            $(`#arrow_preset_${name}`).on("click", () => this.usePreset(name));
        }
        $("#arrow_clear").on("click", () => this.clearPoints());
        // screen2の上にoverlay SVGが重なるため、画面全体でbubbleを受ける。
        const interactionLayer =
            document.querySelector<SVGSVGElement>("#svg_main")!;
        interactionLayer.addEventListener("contextmenu", (event) =>
            event.preventDefault(),
        );
        interactionLayer.addEventListener("mousedown", (event) =>
            this.onMapRightDown(event),
        );
    }

    onChange(callback: (task: Promise<unknown>) => void) {
        this.changeCallback = callback;
    }
    saveParams() {
        this.params.set("ar_on", this.onOff.value);
        this.params.set("ar_Ms", this.keys.join(","));
        this.params.set("ar_siz", String(this.size.value));
        this.params.set("ar_ylw", String(this.yellowWidth.value));
        this.params.set("ar_adj", String(this.temperatureAdjustment.value));
    }
    loadParams() {
        this.onOff.value = readFlag(this.params, "ar_on", "0");
        this.size.value = this.numberParam("ar_siz", 0.9, 0);
        this.yellowWidth.value = this.numberParam("ar_ylw", 20, 0);
        this.temperatureAdjustment.value = this.numberParam("ar_adj", 0);
        this.savedKeys = PRESETS.Lv0.keys.split(",");
        this.keys = this.parseKeys(this.params.get("ar_Ms") || "");
        if (this.keys.length > 0) {
            this.savedKeys = [...this.keys];
        } else if (this.onOff.value === "1") {
            this.keys = [...this.savedKeys];
        }
        if (this.onOff.value !== "1") {
            this.clearSvg();
        }
    }
    onShadeVariableChanged() {
        this.updateAvailability();
        return this.load();
    }
    onInitialTimeChanged() {
        return this.load();
    }
    onForecastTimeChanged() {
        this.render();
        return Promise.resolve();
    }
    onViewChanged() {
        this.render();
        return Promise.resolve();
    }
    async load() {
        const currentSequence = ++this.sequence;
        if (this.onOff.value !== "1" || this.keys.length === 0) {
            this.clearSvg();
            return;
        }
        const model = this.shadeSelector.getParams().model;
        if (!this.supportsCurrentModel()) {
            this.onOff.value = "0";
            this.clearSvg();
            this.setStatus("このモデルには地点別風矢印がありません。");
            return;
        }
        const { ini } = this.initialTime.getParams();
        if (!(await this.initialTime.hasLatest(`${model}A`, ini))) {
            this.onOff.value = "0";
            this.clearSvg();
            this.setStatus("この初期値の風矢印データは作成中です。");
            return;
        }
        this.setStatus("");
        const url = new URL(resourcePath("get_Wind.php"), document.baseURI);
        url.searchParams.set("el", model);
        url.searchParams.set("iniJ", ini.format("YYYYMMDD-HH"));
        url.searchParams.set("M", this.keys.join(","));
        let response: Response;
        try {
            response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = (await response.json()) as WindPointResponse;
            if (currentSequence !== this.sequence) {
                return;
            }
            this.data = data;
            this.render();
        } catch (error) {
            if (currentSequence === this.sequence) {
                this.setStatus("風矢印データを取得できませんでした。");
            }
            throw error;
        }
    }
    private supportsCurrentModel() {
        return ["GSM", "MSM", "LFM", "QMA"].includes(
            this.shadeSelector.getParams().model,
        );
    }
    private updateAvailability() {
        setFeatureAvailability("arrow", this.supportsCurrentModel());
    }

    private onEnabledChanged() {
        if (this.onOff.value === "1" && this.keys.length === 0) {
            this.keys = [...this.savedKeys];
        }
        if (this.onOff.value === "0") {
            this.savedKeys =
                this.keys.length > 0 ? [...this.keys] : this.savedKeys;
            this.clearSvg();
            return;
        }
        this.changeCallback(this.load());
    }
    private usePreset(name: string) {
        const preset = PRESETS[name];
        this.keys = preset.keys.split(",");
        this.savedKeys = [...this.keys];
        this.size.value = preset.size;
        this.onOff.value = "1";
        this.changeCallback(this.load());
    }
    private clearPoints() {
        this.keys = [];
        this.data = {};
        this.clearSvg();
    }
    private onMapRightDown(event: MouseEvent) {
        if (event.button !== 2 || this.onOff.value !== "1") {
            return;
        }
        event.preventDefault();
        const now = performance.now();
        const previous = this.lastRightDown;
        this.lastRightDown = {
            time: now,
            x: event.clientX,
            y: event.clientY,
        };
        if (
            !previous ||
            now - previous.time >= 500 ||
            Math.hypot(
                event.clientX - previous.x,
                event.clientY - previous.y,
            ) >= 8
        ) {
            return;
        }
        this.lastRightDown = null;
        const rotate = document.querySelector<SVGGElement>("#screen2_rotate")!;
        const inverse = rotate.getScreenCTM()?.inverse();
        if (!inverse) {
            return;
        }
        // CSS縮尺、nested SVGのviewBox、地図回転をまとめて逆変換する。
        const point = new DOMPoint(
            event.clientX,
            event.clientY,
        ).matrixTransform(inverse);
        const x = point.x;
        const y = point.y;
        const key = lonLatToMkey(polarToLonLat(x, y));
        if (!this.keys.includes(key)) {
            this.keys.push(key);
            this.changeCallback(this.load());
        }
    }
    private render() {
        this.clearSvg();
        if (this.onOff.value !== "1") {
            return;
        }
        const { model } = this.shadeSelector.getParams();
        const { ft } = this.forecastTime.getParams();
        const time =
            model === "QMA"
                ? ft.toNumber() + 72
                : model === "LFM"
                  ? ft.toNumber() * 2
                  : ft.toNumber();
        const scale =
            (this.mapView.getViewBox().width / getMapMeta(model).width) *
            this.size.value;
        const rotation = this.mapView.getViewBox().deg;
        for (const key of this.keys) {
            const pointData = this.data[key];
            if (!pointData) {
                continue;
            }
            const wind = interpolate(pointData.W, time, false);
            const direction = interpolate(pointData.D, time, true);
            if (!Number.isFinite(wind) || !Number.isFinite(direction)) {
                continue;
            }
            const { lon, x, y } = mkeyToPoint(key);
            const color = this.directionColor(direction);
            const transform = `t(${x},${y}) s(${Math.pow(wind, 0.7) * 7 * scale}) r(${direction + lon - 140})`;
            const arrow = this.arrows.el("use", {
                id: `arrow_${key}`,
                x: -5,
                y: -5,
                width: 10,
                height: 10,
                fill: color,
                stroke: "#fff",
                strokeWidth: 3,
                href: "#point_arrow_symbol",
                "xlink:href": "#point_arrow_symbol",
                transform,
                cursor: "pointer",
            });
            this.attachPointInteraction(arrow, key, x, y);
            const textTransform = `t(${x},${y}) r(${-rotation})`;
            this.values.text(0, 0, String(Math.round(wind))).attr({
                textAnchor: "middle",
                dominantBaseline: "middle",
                fontSize: 100 * scale,
                stroke: "#333",
                strokeWidth: 10 * scale,
                transform: textTransform,
            });
            this.values.text(0, 0, String(Math.round(wind))).attr({
                textAnchor: "middle",
                dominantBaseline: "middle",
                fontSize: 100 * scale,
                fill: "white",
                transform: textTransform,
            });
        }
    }
    private attachPointInteraction(
        arrow: Snap.Element,
        key: string,
        x: number,
        y: number,
    ) {
        const node = arrow.node as unknown as SVGElement;
        node.addEventListener("contextmenu", (event) => event.preventDefault());
        node.addEventListener("mousedown", (start: MouseEvent) => {
            if (start.button !== 2) {
                return;
            }
            start.preventDefault();
            start.stopPropagation();
            const startX = start.clientX;
            const startY = start.clientY;
            const moved = (event: MouseEvent) => {
                const scale = this.mapView.getDisplayScale();
                arrow.attr({
                    transform: `t(${x + (event.clientX - startX) * scale},${y + (event.clientY - startY) * scale})`,
                });
            };
            const ended = (event: MouseEvent) => {
                window.removeEventListener("mousemove", moved);
                window.removeEventListener("mouseup", ended);
                const dx = event.clientX - startX;
                const dy = event.clientY - startY;
                const index = this.keys.indexOf(key);
                if (index < 0) {
                    return;
                }
                if (Math.hypot(dx, dy) < 3) {
                    this.keys.splice(index, 1);
                } else {
                    const displayScale = this.mapView.getDisplayScale();
                    this.keys[index] = lonLatToMkey(
                        polarToLonLat(
                            x + dx * displayScale,
                            y + dy * displayScale,
                        ),
                    );
                }
                this.changeCallback(
                    this.keys.length > 0
                        ? this.load()
                        : Promise.resolve(this.clearSvg()),
                );
            };
            window.addEventListener("mousemove", moved);
            window.addEventListener("mouseup", ended);
        });
    }
    private directionColor(direction: number) {
        const adjustment = this.temperatureAdjustment.value;
        const half = this.yellowWidth.value / 2;
        if (
            direction <= 90 - adjustment - half ||
            direction >= 270 + adjustment + half
        ) {
            return "#66f";
        }
        if (
            direction >= 90 - adjustment + half &&
            direction <= 270 + adjustment - half
        ) {
            return "#f66";
        }
        return "#ff6";
    }
    private installSymbol() {
        if (document.querySelector("#point_arrow_symbol")) {
            return;
        }
        // Keep the definitions in svg_over itself. Downloader serializes that
        // SVG independently, so definitions in svg_main would be lost.
        const overlay = Snap("#svg_over");
        const defs = overlay.el("defs", { id: "point_arrow_defs" });
        const fragment = Snap.parse(
            "<g><symbol id='point_arrow_symbol' viewBox='0 0 80 135'><path d='M55.9,75.4h20.6L40,131.1L3.5,75.4h20.6c0,0-0.9-18.2-1.4-24.3c-1-11.8-5.4-47.2-5.4-47.2H40h22.7c0,0-4.4,35.4-5.4,47.2C56.7,57.1,55.9,75.4,55.9,75.4z' mask='url(#point_arrow_mask)'/></symbol><mask id='point_arrow_mask' maskUnits='objectBoundingBox' maskContentUnits='objectBoundingBox' x='0' y='0' width='1' height='1'><rect x='0' y='0' width='1' height='1' fill='url(#point_arrow_gradient)'/></mask><linearGradient id='point_arrow_gradient' gradientTransform='rotate(90)'><stop offset='0.00' stop-color='#000'/><stop offset='0.25' stop-color='#fff'/></linearGradient></g>",
        );
        const group = fragment.select("g");
        for (const child of group.children()) {
            defs.append(child);
        }
    }
    private clearSvg() {
        this.arrows.clear();
        this.values.clear();
    }
    private setStatus(message: string) {
        $("#arrow_status").text(message);
    }
    private parseKeys(text: string) {
        return [
            ...new Set(text.split(",").filter((key) => /^M\d{6}$/.test(key))),
        ];
    }
    private numberParam(key: string, fallback: number, minimum = -Infinity) {
        if (!this.params.has(key)) {
            return fallback;
        }
        const value = Number(this.params.get(key));
        return Number.isFinite(value) ? Math.max(minimum, value) : fallback;
    }
}

function interpolate(values: number[], time: number, direction: boolean) {
    if (Number.isInteger(time)) {
        return Number(values[time]);
    }
    const lower = Math.floor(time);
    const fraction = time - lower;
    const first = Number(values[lower]);
    let second = Number(values[lower + 1]);
    let adjustedFirst = first;
    if (direction && first - second > 180) {
        second += 360;
    }
    if (direction && first - second < -180) {
        adjustedFirst += 360;
    }
    const result = adjustedFirst * (1 - fraction) + second * fraction;
    return direction ? result % 360 : result;
}

function mkeyToPoint(key: string) {
    const row = Number(key.slice(-6, -3));
    const column = Number(key.slice(-3));
    const lon = 120 + column * 0.0625;
    const lat = 50 - row * 0.05;
    return { lon, ...lonLatToPolar(lon, lat) };
}

function lonLatToPolar(lon: number, lat: number) {
    const central = (140 / 180) * Math.PI;
    const radius = 6371.2;
    const phi = (lat / 180) * Math.PI;
    const lambda = ((lon < 0 ? lon + 360 : lon) / 180) * Math.PI;
    const factor = (2 * radius) / (1 + Math.sin(phi));
    return {
        x: factor * Math.cos(phi) * Math.sin(lambda - central),
        y: factor * Math.cos(phi) * Math.cos(lambda - central),
    };
}

function polarToLonLat(x: number, y: number) {
    const radius = 6371.2;
    return {
        lon: 140 + (Math.atan(x / y) / Math.PI) * 180,
        lat:
            90 -
            ((2 * Math.atan(Math.hypot(x, y) / (2 * radius))) / Math.PI) * 180,
    };
}

function lonLatToMkey({ lon, lat }: { lon: number; lat: number }) {
    const row = Math.max(
        48,
        Math.min(552, Math.floor((50 - lat) / 0.05 + 0.5)),
    );
    const column = Math.max(
        0,
        Math.min(480, Math.floor((lon - 120) / 0.0625 + 0.5)),
    );
    return `M${String(row).padStart(3, "0")}${String(column).padStart(3, "0")}`;
}
